import { useEffect, type JSX } from 'react';
import { ConnectionBanner } from './components/ConnectionBanner.js';
import { Toast } from './components/Toast.js';
import { useT } from './i18n/index.js';
import { AgeGate } from './screens/AgeGate.js';
import { Compound } from './screens/Compound.js';
import { FairPlay } from './screens/FairPlay.js';
import { Kubo } from './screens/Kubo.js';
import { Results } from './screens/Results.js';
import { Settings } from './screens/Settings.js';
import { Splash } from './screens/Splash.js';
import { Store } from './screens/Store.js';
import { Table } from './screens/Table.js';
import { Venue } from './screens/Venue.js';
import { Wallet } from './screens/Wallet.js';
import { useApp } from './state/store.js';

/**
 * Landscape-locked shell. Routing is a plain discriminated union rather than a
 * URL router: the app is a place you walk around (design spec §4), and deep
 * links into a live table would be meaningless anyway.
 */
export function App(): JSX.Element {
  const route = useApp((s) => s.route);
  const boot = useApp((s) => s.boot);
  const setToast = useApp((s) => s.setToast);
  const t = useT();

  useEffect(() => {
    boot().catch((e: Error) => setToast(t('ui.boot.failed', { reason: e.message }), 'warn'));
    // `t` is a fresh closure every render and deliberately not a dependency:
    // boot() mints the guest account, so it runs exactly once.
  }, [boot, setToast]);

  return (
    <div className="app">
      {renderRoute(route)}
      <ConnectionBanner />
      <Toast />
      <RotatePrompt />
    </div>
  );
}

function renderRoute(route: ReturnType<typeof useApp.getState>['route']): JSX.Element {
  switch (route.s) {
    case 'splash':
      return <Splash />;
    case 'agegate':
      return <AgeGate />;
    case 'compound':
      return <Compound />;
    case 'venue':
      return <Venue game={route.game} />;
    case 'table':
      return <Table tableId={route.tableId} />;
    case 'results':
      return <Results matchId={route.matchId} />;
    case 'store':
      return <Store />;
    case 'wallet':
      return <Wallet />;
    case 'settings':
      return <Settings />;
    case 'kubo':
      return <Kubo />;
    case 'fairplay':
      return <FairPlay matchId={route.matchId} />;
  }
}

/** Landscape sensor-lock is a native affordance; on the web we ask. §14. */
function RotatePrompt(): JSX.Element {
  const t = useT();
  return (
    <div className="rotate-prompt" aria-hidden="true">
      <div className="rotate-prompt__inner">
        <div className="rotate-prompt__phone" />
        <p>{t('ui.rotate')}</p>
      </div>
    </div>
  );
}
